import { __read } from "tslib";
import { useMemo, useState } from 'react';
import { isWeekend } from '../../libs/date';
import { useFirstDayOfWeek } from '../../libs/i18n';
import { clamp } from '../../libs/utils';
import { MAX_DATE, MIN_DATE } from './constants';
import { CalendarViewKind, } from './types';
import { clampDate, closestViewDate, createRange, durationInViews, getViewDate, getViews, isEqualDate, isInRange, isSameCell, isSameView, move, nearest, startOfCell, startOfView, subViews, } from './utils';
export function useSingleCalendarState(props) {
    var propValue = props.value, _a = props.defaultFocusedDate, defaultFocusedDate = _a === void 0 ? propValue : _a, onChange = props.onChange, _b = props.autoFocus, autoFocus = _b === void 0 ? false : _b, _c = props.readOnly, readOnly = _c === void 0 ? false : _c, _d = props.disabled, disabled = _d === void 0 ? false : _d, _e = props.min, propMin = _e === void 0 ? MIN_DATE : _e, _f = props.max, propMax = _f === void 0 ? MAX_DATE : _f, _g = props.viewsCount, viewsCount = _g === void 0 ? 1 : _g, _h = props.minCalendarView, minCalendarView = _h === void 0 ? 'day' : _h, _j = props.defaultCalendarView, defaultCalendarView = _j === void 0 ? minCalendarView : _j, _k = props.maxCalendarView, maxCalendarView = _k === void 0 ? 'year' : _k;
    var min = useMemo(function () { return new Date(propMin); }, [propMin]);
    var max = useMemo(function () { return new Date(propMax); }, [propMax]);
    var value = useMemo(function () { return (propValue ? new Date(propValue) : undefined); }, [propValue]);
    var firstDayOfWeek = useFirstDayOfWeek();
    var _l = __read(useState(function () { return normalizeView(defaultCalendarView); }), 2), activeView = _l[0], setActiveView = _l[1];
    var _m = __read(useState(autoFocus), 2), isCalendarFocused = _m[0], focusCalendar = _m[1];
    var _o = __read(useState(function () {
        return normalizeFocusedDate(defaultFocusedDate ? new Date(defaultFocusedDate) : new Date());
    }), 2), focusedDate = _o[0], setFocusedDate = _o[1];
    var _p = __read(useState(function () { return normalizeBaseDate(activeView, focusedDate); }), 2), baseDate = _p[0], setBaseDate = _p[1];
    function normalizeFocusedDate(date) {
        return startOfCell(minCalendarView, clampDate(date, min, max));
    }
    function normalizeBaseDate(view, date) {
        var maxDate = subViews(view, max, viewsCount - 1);
        var candidate = startOfView(view, clampDate(date, min, min > maxDate ? min : maxDate));
        return getViewDate(view, candidate, date);
    }
    function normalizeView(view) {
        var key = clamp(CalendarViewKind[view], CalendarViewKind[minCalendarView], CalendarViewKind[maxCalendarView]);
        return CalendarViewKind[key];
    }
    function moveView(view, offset) {
        if (offset === void 0) { offset = 0; }
        var key = CalendarViewKind[view] + offset;
        var nextView = CalendarViewKind[key];
        return normalizeView(nextView !== null && nextView !== void 0 ? nextView : view);
    }
    function navigateTo(date) {
        var nextBaseDate = getViewDate(activeView, date, focusedDate);
        if (isEqualDate(baseDate, nextBaseDate)) {
            return;
        }
        setBaseDate(nextBaseDate);
        var nextFocusedDate = normalizeFocusedDate(nearest(activeView, focusedDate, nextBaseDate, viewsCount));
        if (isEqualDate(focusedDate, nextFocusedDate)) {
            return;
        }
        setFocusedDate(nextFocusedDate);
    }
    function focusTo(view, date) {
        var nextFocusedDate = clampDate(startOfCell(minCalendarView, date), min, max);
        if (isEqualDate(focusedDate, nextFocusedDate)) {
            return;
        }
        setFocusedDate(nextFocusedDate);
        var nextBaseDate = getViewDate(view, closestViewDate(view, baseDate, viewsCount, nextFocusedDate), nextFocusedDate);
        if (isEqualDate(baseDate, nextBaseDate)) {
            return;
        }
        setBaseDate(nextBaseDate);
    }
    function setViewAndFocusedDate(view, date) {
        if (CalendarViewKind[view] < CalendarViewKind[minCalendarView] ||
            CalendarViewKind[view] > CalendarViewKind[maxCalendarView]) {
            return;
        }
        if (view !== activeView) {
            setActiveView(view);
        }
        var nextFocusedDate = normalizeFocusedDate(date);
        if (!isEqualDate(focusedDate, nextFocusedDate)) {
            setFocusedDate(nextFocusedDate);
        }
        var offset = durationInViews(activeView, baseDate, normalizeBaseDate(activeView, nextFocusedDate));
        var nextBaseDate = subViews(view, normalizeBaseDate(view, nextFocusedDate), offset);
        if (!isEqualDate(baseDate, nextBaseDate)) {
            setBaseDate(nextBaseDate);
        }
    }
    function setView(view) {
        setViewAndFocusedDate(view, focusedDate);
    }
    function focusDate(date) {
        focusTo(activeView, date);
    }
    function selectDate(date) {
        if (readOnly || disabled || !isInRange(activeView, date, createRange(min, max))) {
            return;
        }
        var nextView = moveView(activeView, -1);
        if (nextView !== activeView) {
            setViewAndFocusedDate(nextView, date);
        }
        else {
            focusTo(nextView, date);
            if (!readOnly && nextView === minCalendarView) {
                onChange === null || onChange === void 0 ? void 0 : onChange({ value: date });
            }
        }
    }
    function getCellState(cellValue, viewDate) {
        var today = new Date();
        var sameView = isSameView(activeView, cellValue, viewDate);
        var isDisabled = disabled || !isInRange(activeView, cellValue, createRange(min, max));
        var isFocused = sameView && isSameCell(activeView, cellValue, focusedDate);
        var isSelected = value ? isSameCell(activeView, cellValue, value) : false;
        var isToday = isSameCell(activeView, cellValue, today);
        var cellState = {
            isDisabled: isDisabled,
            isFocused: isFocused,
            isSameView: sameView,
            isSelected: isSelected,
            isToday: isToday,
            isWeekend: activeView === 'day' && isWeekend(cellValue),
        };
        return cellState;
    }
    function moveDate(date, action) {
        return move(activeView, date, action, { firstDayOfWeek: firstDayOfWeek });
    }
    function canNavigateTo(date) {
        var minViewDate = startOfView(activeView, min);
        var maxViewDate = new Date(Math.max(minViewDate.getTime(), startOfView(activeView, subViews(activeView, max, viewsCount - 1)).getTime()));
        return isInRange(activeView, date, createRange(minViewDate, maxViewDate));
    }
    var views = useMemo(function () {
        return getViews(activeView, baseDate, viewsCount, { firstDayOfWeek: firstDayOfWeek });
    }, [baseDate, viewsCount, activeView, firstDayOfWeek]);
    return {
        mode: 'single',
        value: value,
        isCalendarFocused: isCalendarFocused,
        baseDate: baseDate,
        focusedDate: focusedDate,
        activeView: activeView,
        views: views,
        focusCalendar: focusCalendar,
        focusDate: focusDate,
        selectDate: selectDate,
        getCellState: getCellState,
        moveDate: moveDate,
        setView: setView,
        canNavigateTo: canNavigateTo,
        navigateTo: navigateTo,
        moveView: moveView,
    };
}
